import React from 'react';
import PropTypes from 'prop-types';
import { withStyles, styled } from '../../styling';
import { parseSystemStyle } from '../../system';
import { Box } from '../Box';
import { Text } from '../Text';
import { EMPTY_FUNC } from '../../utils';

// TODO: remove boolean shortcuts. makes overriding too difficult (buttongrup is example of this wher button group will override a boolean prop by passing size)
const propTypes = {
  children: PropTypes.node, // the text string for button name or Text component
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  size: PropTypes.oneOf(['small', 'medium', 'large']), // default is medium
  large: PropTypes.bool,
  medium: PropTypes.bool,
  small: PropTypes.bool,
  outlined: PropTypes.bool,
  contained: PropTypes.bool,
  text: PropTypes.bool,
  regular: PropTypes.bool, // make text regular and not bold
  color: PropTypes.string, // affects the background/border color and text/icon color
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  fullWidth: PropTypes.bool, // fills its container width,
  fullHeight: PropTypes.bool, // fill its container height,
  disabled: PropTypes.bool,
};

const filterProps = [
  'variant',
  'color',
  'large',
  'medium',
  'small',
  'outlined',
  'contained',
  'text',
  'regular',
  'fullWidth',
  'fullHeight',
];

const defaultProps = {
  variant: null,
  contained: true,
  outlined: false,
  text: false,
  size: null,
  medium: true,
  large: false,
  small: false,
  color: null,
  regular: false,
  startIcon: null,
  endIcon: null,
  fullWidth: false,
  fullHeight: false,
  disabled: false,
  onPress: EMPTY_FUNC,
};

const StartIcon = styled(Box, { name: 'StartIcon', filterProps: ['size'] })(
  ({ theme, size }) => ({
    marginRight: theme.spacing(1),
    marginLeft: size === 'small' ? theme.spacing(-0.25) : theme.spacing(-0.5),
  })
);

const EndIcon = styled(Box, { name: 'EndIcon', filterProps: ['size'] })(
  ({ theme, size }) => ({
    marginLeft: theme.spacing(1),
    marginRight: size === 'small' ? theme.spacing(-0.25) : theme.spacing(-0.5),
  })
);

const Button = withStyles(props => {
  const {
    theme,
    size,
    large,
    small,
    color,
    variant,
    outlined,
    text,
    regular,
    fullWidth,
    fullHeight,
  } = props;

  let buttonSize = size;
  if (!size || !theme.button.sizes[size]) {
    if (large) {
      buttonSize = 'large';
    } else if (small) {
      buttonSize = 'small';
    } else {
      buttonSize = 'medium';
    }
  }

  let buttonVariant = variant;
  if (!variant || !theme.button[variant]) {
    if (outlined) {
      buttonVariant = 'outlined';
    } else if (text) {
      buttonVariant = 'text';
    } else {
      buttonVariant = 'contained';
    }
  }

  let textStyle = { };
  let buttonStyle = { };
  if (buttonVariant === 'contained') {
    const c = color || '$primary';
    buttonStyle.backgroundColor = c;
    textStyle.color = theme.colors.on(parseSystemStyle(c));
  } else if (buttonVariant === 'outlined') {
    const c = color || '$secondary';
    buttonStyle.borderColor = c;
    textStyle.color = c;
  } else {
    textStyle.color = color || '$text';
  }

  if (regular) {
    textStyle.fontWeight = '$regular';
  }

  if (fullWidth) {
    buttonStyle.width = '100%';
  }

  if (fullHeight) {
    buttonStyle.height = '100%';
  }

  const style = {
    root: {
      ...theme.button.base,
      ...theme.button.sizes[buttonSize],
      ...theme.button[buttonVariant],
      ...buttonStyle,
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
    },
    [Text]: {
      ...theme.text.button[buttonSize],
      ...textStyle,
    }
  }
  return style;
}, { name: 'Button', filterProps })
(React.forwardRef(function Button({
  styles,
  children,
  accessibility,
  startIcon: startIconProp,
  endIcon: endIconProp,
  size,
  disabled = false,
  onPress = EMPTY_FUNC,
  ...props
}, ref) {

  const startIcon = startIconProp && (
      <StartIcon size={size}>{startIconProp}</StartIcon>
  );

  const endIcon = endIconProp && <EndIcon size={size}>{endIconProp}</EndIcon>;

  return (
    <Box
      ref={ref}
      accessibility={{ accessibilityRole: 'button', ...accessibility }}
      disabled={disabled}
      onPress={onPress}
      {...props}
    >
      {startIcon}
      {typeof children === 'string' ? (
        <Text {...{ styles }} selectable={false}>
          {children}
        </Text>
      ) : (
        children
      )}
      {endIcon}
    </Box>
  );
}));


Button.propTypes = propTypes;

export { Button }
